import React, { useEffect, useState, useCallback } from 'react';
import { Button, Select, TextBox, Checkbox } from "@veneer/core";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import { 
  getFieldError, 
  isValidEmail, 
  isValidPhoneNumber, 
  getValidatedNumberInput,
  onSearchDropDown,
  validateFields,
  scrollToErrorField,
  isValidField
} from 'utils';
import { addressvalidatedcountryexclusionlst } from "../../../../resources/exclusion-country-validation";
import { HelpModalPopup } from "@components/home-page/shared-components/modal-popup/help-modal-popup";
import { POST_validateaddress } from "@constants/end-points";
import { apiPost } from "@services/service";
import { setLoading } from "@store/reducers/loadingSlice";
import { fetchContactDetails } from "@services/fetch-contact-details-info";

interface PartShippingAddressModalProps {
    countryList: any[];
    countryCodes: any[];
    savedContactsDropDown?: any[];
    initialData?: any;
    onSave: (data: any) => void;
    onClose: () => void;
}

const PartShippingAddressModal: React.FC<PartShippingAddressModalProps> = ({ 
    countryList, 
    countryCodes, 
    savedContactsDropDown,
    onSave, 
    onClose,
    initialData 
}) => {   
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { IsTaxEnabledCountry } = useAppSelector((state) => state.createCaseAPI);
    
    const defaultRequiredFields = [
        'FirstName', 'LastName', 'countryId', 'PhoneNumber', 'Email',
        'company', 'AddressLine1', 'City', 'State', 'ZipCode'
    ];

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [filteredCountryList, setFilteredCountryList] = useState(countryList || []);
    const [filteredCountryCodeList, setFilteredCountryCodeList] = useState(countryCodes || []);
    const [filteredAltContacts, setFilteredAltContacts] = useState<any[]>([]);
    const [contryEnable, setContryEnable] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState(false);
    const [popupTilte, setPopupTitle] = useState<string>("");

    const [form, setForm] = useState({ 
        UserId: [''],
        FirstName: '', 
        LastName: '', 
        countryId: [''],
        PhoneNumber: '',
        Extension: '',
        Email: '',
        company: '',
        Mailstop: '',
        AddressLine1: '',
        AddressLine2: '',
        City: '',
        State: '',
        ZipCode: '',
        partsShippingTaxId: '',
        partsShippingTaxOfficeName: '',
        EquipmentCountryName: '',
        validatedAddressBtn: 'on'
    });
    useEffect(() => {
        if (initialData && Object.keys(initialData).length > 0) {
            setForm(initialData);
        } else {
            setForm(form);
        }
        }, [initialData]);
    useEffect(() => {
        if (savedContactsDropDown?.length) {
            let savedContacts: any = [
                {
                    label: t("HPI.Dropdown.SelectSavedContact", {
                        defaultValue: "create a new contact",
                    }),
                    value: "-1",
                },
                ...savedContactsDropDown,
            ];
            setFilteredAltContacts(savedContacts);
        }
    }, [savedContactsDropDown, t]);

    useEffect(() => {
        setFilteredCountryList(countryList || []);
    }, [countryList]);

    useEffect(() => {
        setFilteredCountryCodeList(countryCodes || []);
    }, [countryCodes]);

    const fetchContactInfo = useCallback(async (payload: any) => {
        try {
            const data = await fetchContactDetails(dispatch, payload);
            setForm((prevData) => ({ ...prevData, ...data }));
            const isExcluded =
                !!data?.countryName &&
                addressvalidatedcountryexclusionlst.some(
                    (country: any) =>
                        country.toLowerCase() === data?.countryName.toLowerCase()
                );
            if (isExcluded) {
                setForm((prevData) => ({ ...prevData, validatedAddressBtn: "on" }));
            } else {
                setForm((prevData) => ({ ...prevData, validatedAddressBtn: "off" }));
            }
            setForm((prevData) => ({
                ...prevData,
                EquipmentCountryName: data?.countryName,
            }));
            setContryEnable(isExcluded);
        } catch (err) {
            setContryEnable(false);
        }
    }, [dispatch]);

    const selectSavedClearHandler = () => {
        setForm({
            UserId: [''],
            FirstName: '',
            LastName: '',
            countryId: [''],
            PhoneNumber: '',
            Extension: '',
            Email: '',
            company: '',
            Mailstop: '',
            AddressLine1: '',
            AddressLine2: '',
            City: '',
            State: '',
            ZipCode: '',
            partsShippingTaxId: '',
            partsShippingTaxOfficeName: '',
            EquipmentCountryName: '',
            validatedAddressBtn: 'on'
        });
    };

    const validateField = (name: string, value: string | boolean) => {
        const validations: {
            [key: string]: { fn: (val: any) => boolean; msg: string };
        } = {
            Email: {
                fn: isValidEmail,
                msg: t("HPI.ErrorMessage.InvalidEmailFormat", {
                    defaultValue: "Invalid email format",
                }),
            },
            PhoneNumber: {
                fn: isValidPhoneNumber,
                msg: t("HPI.ErrorMessage.InvalidNumber", {
                    defaultValue: "Phone number must be 8-15 digits long and contain only numbers.",
                }),
            },
        };

        let error = "";
        if (!value && defaultRequiredFields.includes(name)) {
            error = t('HPI.ErrorMessage.RequiredFields', {
                defaultValue: 'This field is required.'
            });
        } else if (validations[name] && !validations[name].fn(value)) {
            error = validations[name].msg;
        }

        setErrors((prevState) => ({
            ...prevState,
            [name]: error,
        }));
    };

    const onSelectChangeHandler = (selectedOption: any, k: string) => {
        if (selectedOption?.value !== '-1') {
            setForm((prevData) => ({ 
                ...prevData, 
                [k]: k === 'countryId' ? [selectedOption?.value] : [selectedOption?.value]
            }));

            if (k === 'UserId' && selectedOption.value !== '-1') {
                const payload = {
                    contactid: selectedOption.value,
                };
                fetchContactInfo(payload);
                setContryEnable(false);
            }

            if (k === 'countryId' && selectedOption.value !== '-1') {
                setForm((prevData) => ({
                    ...prevData,
                    EquipmentCountryName: selectedOption?.label,
                }));

                const isExcluded = addressvalidatedcountryexclusionlst.some(
                    (country: any) =>
                        country.toLowerCase() === selectedOption.label.toLowerCase() ||
                        country.toLowerCase() === selectedOption.CountryName?.toLowerCase()
                );

                setForm((prevData) => ({ 
                    ...prevData, 
                    validatedAddressBtn: isExcluded ? "on" : "off" 
                }));
                setContryEnable(isExcluded);
            }
        } else {
            selectSavedClearHandler();
            setForm((prevData) => ({ ...prevData, [k]: ['-1'] }));
        }
        validateField(k, selectedOption?.value);
    };

    const handleInputChange = (v: any, event: React.ChangeEvent<HTMLInputElement>) => {
        const { name } = event.target;
        setForm((prevData) => ({ ...prevData, [name]: v }));
        
        if (['PhoneNumber', 'Email'].includes(name)) {
            setErrors((prevState) => ({
                ...prevState,
                [name]: '',
            }));
        } else {
            validateField(name, v);
        }
    };

    const clearSelectHandler = (field: string) => {
        setForm((prevData) => ({ 
            ...prevData, 
            [field]: field === 'countryId' ? [''] : '',
            ...(field === "countryId" ? {
                EquipmentCountryName: "",
                validatedAddressBtn: "off",
            } : {}),
        }));
        if (field === "countryId") {
            setContryEnable(false);
        }
        validateField(field, '');
    };

    const handleCallBack = (title: string) => {
        setPopupTitle(title);
        setIsOpen(true);
    };

    const popupHeader = () => {
        return (
            <p className="popup-header">
                <img
                    src="https://logos-world.net/wp-content/uploads/2020/11/HP-Logo.png"
                    alt=""
                    style={{ height: "46px", width: "auto" }}
                />
                <span>{popupTilte}</span>
            </p>
        );
    };

    const validateForm = () => {
        let formIsValid = true;
        const newErrors: Record<string, string> = {};

        for (const field of defaultRequiredFields) {
            const value = form[field as keyof typeof form];
            const valueToValidate = Array.isArray(value) ? value.join(' ') : value;
            
            if (!valueToValidate || (Array.isArray(value) && value[0] === '')) {
                newErrors[field] = t("HPI.ErrorMessage.RequiredFields", {
                    defaultValue: "This field is required.",
                });
                formIsValid = false;
            }
        }

        // Additional validations
        if (form.Email && !isValidEmail(form.Email)) {
            newErrors.Email = t("HPI.ErrorMessage.InvalidEmailFormat", {
                defaultValue: "Invalid email format",
            });
            formIsValid = false;
        }

        if (form.PhoneNumber && !isValidPhoneNumber(form.PhoneNumber)) {
            newErrors.PhoneNumber = t("HPI.ErrorMessage.InvalidNumber", {
                defaultValue: "Phone number must be 8-15 digits long and contain only numbers.",
            });
            formIsValid = false;
        }

        setErrors(newErrors);
        return formIsValid;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        if (validateForm()) {
            onSave(form);
            onClose();
        }
    };

    return (
        <form className="part-shipping-address-modal" onSubmit={handleSubmit}>
            <div className="mb-4">
                <h4>{t("HPI.CreateCase.PartsShippingAddress", { defaultValue: "Parts Shipping Address" })}</h4>
            </div>

            <Row className="mt-4">
                <Col md={6}>
                    <Select
                        options={filteredAltContacts || []}
                        label={`${t(
                            "HPI.CreateCase.Contactinformation.savedContact",
                            { defaultValue: "Saved Contact" }
                        )}${t(
                            "HPI.CreateCase.Contactinformation.choosefromlist",
                            {
                                defaultValue:
                                    "Choose a saved contact or create a new contact",
                            }
                        )}`}
                        helperTextVisibility="auto"
                        separateLabel
                        name="UserId"
                        onChange={(selectedOption: any) =>
                            onSelectChangeHandler(selectedOption, "UserId")
                        }
                        value={form?.UserId}
                        placeholder={t("HPI.Dropdown.DefaultText", {
                            defaultValue: "Please select",
                        })}
                        onClear={() => {
                            selectSavedClearHandler();
                        }}
                        {...getFieldError(errors, "UserId")}
                        className="textbox"
                    />
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <TextBox
                        separateLabel
                        label={t("HPI.CreateCase.ShippingContactFirstName", {
                            defaultValue: "Shipping contact first name",
                        })}
                        required
                        name="FirstName"
                        placeholder={t("HPI.CreateCase.Placeholder.EnterShippingContactFirstName", {
                            defaultValue: "Enter shipping contact first name"
                        })}
                        className="textbox"
                        value={form.FirstName}
                        onChange={(v: any, e) => handleInputChange(v, e)}
                        error={!!errors.FirstName}
                        helperText={errors.FirstName}
                    />
                </Col>
                <Col md={6}>
                    <TextBox
                        separateLabel
                        label={t("HPI.CreateCase.ShippingContactLastName", {
                            defaultValue: "Shipping contact last name",
                        })}
                        required
                        name="LastName"
                        placeholder={t("HPI.CreateCase.Placeholder.EnterShippingContactLastName", {
                            defaultValue: "Enter shipping contact last name"
                        })}
                        className="textbox"
                        value={form.LastName}
                        onChange={(v: any, e) => handleInputChange(v, e)}
                        error={!!errors.LastName}
                        helperText={errors.LastName}
                    />
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <Select
                        options={filteredCountryList}
                        showSearch
                        label={t("HPI.CreateCase.address.CountryRegion", {
                            defaultValue: "Country/Region",
                        })}
                        helperTextVisibility="auto"
                        separateLabel
                        required
                        name="countryId"
                        placeholder={t("HPI.ProfilePage.cs.SelectCountry", {
                            defaultValue: "Select country/Region",
                        })}
                        onClear={() => clearSelectHandler("countryId")}
                        value={form?.countryId}
                        onSearch={(value: any) => {
                            let filtered: any = onSearchDropDown(countryList, value);
                            setFilteredCountryList(filtered);
                        }}
                        clearIcon={false}
                        onChange={(selectedOption: any) =>
                            onSelectChangeHandler(selectedOption, "countryId")
                        }
                        {...getFieldError(errors, "countryId")}
                        className="textbox"
                    />
                </Col>
                <Col md={6}>
                    <TextBox
                        value={form.Extension}
                        label={t("HPI.CreateCase.Contactinformation.Extension", {
                            defaultValue: "Extension"
                        })}
                        name="Extension"
                        placeholder={t("HPI.CreateCase.Placeholder.EnterExtension", {
                            defaultValue: "Enter extension"
                        })}
                        separateLabel
                        className="textbox"
                        onChange={(v: any, e) => {
                            const value = getValidatedNumberInput(v, 5);
                            if (value.length <= 5) {
                                handleInputChange(value, e);
                            }
                        }}
                    />
                </Col>
            </Row>

            <Row>
                <Col lg={2} md={6}>
                    <Select
                        separateLabel
                        options={filteredCountryCodeList}
                        showSearch
                        name="countryCode"
                        label={t("HPI.ProfilePage.cs.SelectCountryCode", {
                            defaultValue: "Code",
                        })}
                        placement="bottom"
                        placeholder={t("HPI.ProfilePage.cs.SelectCountryCode", {
                            defaultValue: "Code",
                        })}
                        required
                        value={form?.countryId}
                        onChange={(selectedOption: any) =>
                            onSelectChangeHandler(selectedOption, "countryId")
                        }
                        helperTextVisibility="auto"
                        onSearch={(value: any) => {
                            let filtered: any = onSearchDropDown(countryCodes, value);
                            setFilteredCountryCodeList(filtered);
                        }}
                        
                        clearIcon={false}
                        {...getFieldError(errors, "countryId")}
                        className="textbox country-code"
                    />
                </Col>
                <Col lg={4} md={6}>
                    <label>
                        {t("HPI.CreateCase.Contactinformation.Phonenumber", {
                            defaultValue: "Phone Number",
                        })}
                    </label>
                    <span className="error">*</span>
                    <HelpModalPopup
                        align={"start"}
                        show={isOpen}
                        onClose={() => setIsOpen(false)}
                        title={popupHeader()}
                        callback={() =>
                            handleCallBack(
                                t("HPI.CreateCase.Contactinformation.Phonenumber", {
                                    defaultValue: "Phone Number",
                                })
                            )
                        }
                    >
                        <p>
                            {t("HPI.Profilepage.PhoneNumberToolTip.body", {
                                defaultValue:
                                    "Please enter no more than 15 numerals, starting with country code (do not use " +
                                    " or any other characters)",
                            })}
                        </p>
                    </HelpModalPopup>
                    <TextBox
                        placeholder={t("HPI.CreateCase.Placeholder.EnterPhoneNumber", {
                            defaultValue: "Enter phone number"
                        })}
                        required
                        value={form.PhoneNumber}
                        name="PhoneNumber"
                        onChange={(v: any, e) => {
                            const value = getValidatedNumberInput(v, 15);
                            if (value.length <= 15) {
                                handleInputChange(value, e);
                            }
                        }}
                        error={!!errors.PhoneNumber}
                        helperText={errors.PhoneNumber}
                        className="textbox"
                    />
                </Col>
                <Col md={6}>
                    <TextBox
                        separateLabel
                        label={t("HPI.CreateCase.Contactinformation.Email", {
                            defaultValue: "Email",
                        })}
                        type="email"
                        name="Email"
                        required
                        placeholder={t("HPI.CreateCase.Placeholder.EnterEmail", {
                            defaultValue: "Enter email"
                        })}
                        value={form.Email}
                        onChange={(v: any, e) => handleInputChange(v, e)}
                        error={!!errors.Email}
                        helperText={errors.Email}
                        className="textbox"
                    />
                </Col>
            </Row>

            {!window.IsIndividualRole && (
                <Row>
                    <Col md={6}>
                        <TextBox
                            separateLabel
                            label={t("HPI.CreateCase.address.Company", {
                                defaultValue: "Company",
                            })}
                            type="text"
                            name="company"
                            required
                            placeholder={t("HPI.CreateCase.Placeholder.EnterCompany", {
                                defaultValue: "Enter company",
                            })}
                            value={form.company}
                            onChange={(v: any, e) => handleInputChange(v, e)}
                            error={!!errors.company}
                            helperText={errors.company}
                            className="textbox"
                        />
                    </Col>
                    <Col md={6}>
                        <TextBox
                            separateLabel
                            label={t("HPI.CreateCase.address.Mailstop", {
                                defaultValue: "Mail Stop",
                            })}
                            type="text"
                            name="Mailstop"
                            placeholder={t("HPI.CreateCase.Placeholder.EnterMailStop", {
                                defaultValue: "Enter mail stop",
                            })}
                            value={form.Mailstop}
                            onChange={(v: any, e) => handleInputChange(v, e)}
                            className="textbox"
                        />
                    </Col>
                </Row>
            )}

            <Row>
                <Col md={6}>
                    <TextBox
                        className="textbox"
                        separateLabel
                        label={t("HPI.CreateCase.address.AddressLine1", {
                            defaultValue: "Address Line 1",
                        })}
                        type="text"
                        name="AddressLine1"
                        required
                        placeholder={t("HPI.CreateCase.Placeholder.EnterAddressLine1", {
                            defaultValue: "Enter address line 1"
                        })}
                        onChange={(v: any, e) => handleInputChange(v, e)}
                        value={form.AddressLine1}
                        error={!!errors.AddressLine1}
                        helperText={errors.AddressLine1}
                    />
                </Col>
                <Col md={6}>
                    <TextBox
                        className="textbox"
                        separateLabel
                        label={t("HPI.CreateCase.address.AddressLine2", {
                            defaultValue: "Address Line 2",
                        })}
                        type="text"
                        name="AddressLine2"
                        placeholder={t("HPI.CreateCase.Placeholder.EnterAddressLine2", {
                            defaultValue: "Enter address line 2"
                        })}
                        value={form.AddressLine2}
                        onChange={(v: any, e) => handleInputChange(v, e)}
                        error={!!errors.AddressLine2}
                        helperText={errors.AddressLine2}
                    />
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <TextBox
                        separateLabel
                        label={t("HPI.CreateCase.address.City/Town", {
                            defaultValue: "City/Town",
                        })}
                        type="text"
                        required
                        name="City"
                        placeholder={t("HPI.CreateCase.Placeholder.EnterCity/Town", {
                            defaultValue: "Enter city/town",
                        })}
                        value={form.City}
                        onChange={(v: any, e) => handleInputChange(v, e)}
                        error={!!errors.City}
                        helperText={errors.City}
                        className="textbox"
                    />
                </Col>
                <Col md={6}>
                    <TextBox
                        separateLabel
                        label={t("HPI.CreateCase.address.State/Province", {
                            defaultValue: "State/Province",
                        })}
                        required
                        type="text"
                        name="State"
                        placeholder={t("HPI.CreateCase.Placeholder.EnterState/Province", {
                            defaultValue: "Enter state/province"
                        })}
                        value={form.State}
                        onChange={(v: any, e) => handleInputChange(v, e)}
                        error={!!errors.State}
                        helperText={errors.State}
                        className="textbox"
                    />
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <TextBox
                        separateLabel
                        label={t("HPI.CreateCase.address.ZipCode", {
                            defaultValue: "Zip code and Postal code",
                        })}
                        type="text"
                        name="ZipCode"
                        placeholder={t("HPI.CreateCase.Placeholder.EnterZipCode/postalCode", {
                            defaultValue: "Enter zip code/postal code"
                        })}
                        value={form.ZipCode}
                        required
                        onChange={(v: any, e) => handleInputChange(v, e)}
                        error={!!errors.ZipCode}
                        helperText={errors.ZipCode}
                        className="textbox"
                    />
                </Col>
            </Row>

            {IsTaxEnabledCountry && IsTaxEnabledCountry.includes(form?.countryId[0]) && (
                <Row>
                    <Col md={6}>
                        <TextBox
                            separateLabel
                            label={t("HPI.CreateCase.TaxId", {
                                defaultValue: "Tax Id",
                            })}
                            required
                            type="text"
                            name="partsShippingTaxId"
                            placeholder={t("HPI.CreateCase.Placeholder.EnterTaxId", {
                                defaultValue: "Enter tax Id",
                            })}
                            value={form.partsShippingTaxId}
                            onChange={(v: any, e) => handleInputChange(v, e)}
                            error={!!errors.partsShippingTaxId}
                            helperText={errors.partsShippingTaxId}
                            className="textbox"
                        />
                    </Col>
                    <Col md={6}>
                        <TextBox
                            separateLabel
                            label={t("HPI.CreateCase.TaxOfficeNames", {
                                defaultValue: "Tax Office Names",
                            })}
                            required
                            type="text"
                            name="partsShippingTaxOfficeName"
                            placeholder={t("HPI.CreateCase.Placeholder.EnterTaxOfficeNames", {
                                defaultValue: "Enter tax office names"
                            })}
                            value={form.partsShippingTaxOfficeName}
                            onChange={(v: any, e) => handleInputChange(v, e)}
                            error={!!errors.partsShippingTaxOfficeName}
                            helperText={errors.partsShippingTaxOfficeName}
                            className="textbox"
                        />
                    </Col>
                </Row>
            )}

            <Row className="mt-3">
                <Col className="d-flex justify-content-end gap-2">
                    <Button appearance="secondary" type="button" onClick={onClose}>
                        {t("HPI.CreateCase.Cancel", { defaultValue: "Cancel" })}
                    </Button>
                    <Button type="submit">
                        {t("HPI.CreateCase.Save", { defaultValue: "Save" })}
                    </Button>
                </Col>
            </Row>
        </form>
    );
};

export default PartShippingAddressModal;